/**
 * Sidebar Component
 * Main navigation sidebar for the workspace application.
 *
 * Ported from v0.26 legacy app -- adapted for verisa_v1 project structure.
 *
 * Features:
 * - The installation logo and product name at top (from the BrandingProvider)
 * - Search bar (placeholder for MVP)
 * - Navigation sections: Agents, Report History
 * - LocalePicker + user info at bottom
 * - Responsive: collapsible on mobile
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLocation } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { colors } from '../../theme/colors';
import NavItem from './NavItem';
import type { NavItemData } from './NavItem';
import { useAuth } from '../../store/AuthContext';
import { LocalePicker } from '../shared/LocalePicker';
import { getAgent } from '../../api/workspace';
import { AccessibilityDrawer } from '../accessibility';
import { FeatureGate } from '../../feature-gate';
import { useBranding } from '../../branding/BrandingProvider';
import { DefaultBranding } from '../../branding/defaults';

/** Sidebar width when expanded */
export const SIDEBAR_WIDTH = 240;
/** Sidebar width when collapsed (tablet mode) */
export const SIDEBAR_COLLAPSED_WIDTH = 56;

interface SidebarProps {
  /** Whether the mobile drawer is open */
  mobileOpen?: boolean;
  /** Handler to toggle mobile drawer */
  onMobileToggle?: () => void;
}

/**
 * Dashboard icon SVG
 */
const DashboardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

/**
 * Agents icon SVG
 */
const AgentsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    color="currentColor"
  >
    <path
      d="M11.5513 2.25C13.4654 2.24999 14.9971 2.24998 16.2271 2.37248C16.1143 2.66571 16.0303 2.86761 15.9766 2.98438C15.8159 3.05836 15.502 3.18172 15.003 3.36637L14.3059 3.6243C13.5211 3.91473 13 4.66312 13 5.5C13 6.33688 13.5211 7.08528 14.3059 7.3757L15.003 7.63363C15.502 7.81828 15.8198 7.94164 15.9805 8.01563C16.0545 8.17626 16.1817 8.49803 16.3664 8.99705L16.6243 9.69407C16.9147 10.4789 17.6631 11 18.5 11C19.3369 11 20.0853 10.4789 20.3757 9.69407L20.6336 8.99705C20.8183 8.49803 20.9416 8.17626 21.0156 8.01562C21.1323 7.96186 21.3341 7.88203 21.6272 7.77156C21.75 9.01601 21.75 10.6414 21.75 12.5572C21.75 14.7479 21.75 16.4686 21.5694 17.812C21.3843 19.1886 20.9973 20.2809 20.1391 21.1391C19.2809 21.9973 18.1886 22.3843 16.812 22.5694C15.4686 22.75 13.7479 22.75 11.5572 22.75C9.36646 22.75 7.53144 22.75 6.18802 22.5694C4.81137 22.3843 3.71911 21.9973 2.86091 21.1391C2.00272 20.2809 1.61568 19.1886 1.43059 17.812C1.24998 16.4686 1.24999 14.7479 1.25 12.5572V12.5572C1.24999 10.3665 1.24998 8.53144 1.43059 7.18802C1.61568 5.81137 2.00272 4.71911 2.86091 3.86091C3.71911 3.00272 4.81137 2.61568 6.18802 2.43059C7.53144 2.24998 9.36056 2.24999 11.5513 2.25H11.5513ZM13.9951 10C13.9951 11.3807 12.8742 12.5 11.4915 12.5C10.1089 12.5 8.98797 11.3807 8.98797 10C8.98797 8.61929 10.1089 7.5 11.4915 7.5C12.8742 7.5 13.9951 8.61929 13.9951 10ZM16.6512 16.8779C14.3633 12.874 8.63674 12.874 6.34882 16.8779C6.21617 17.11 6.21713 17.3952 6.35132 17.6264C6.48551 17.8577 6.73265 18 7 18H16C16.2674 18 16.5145 17.8577 16.6487 17.6264C16.7829 17.3952 16.7838 17.11 16.6512 16.8779Z"
      fill="currentColor"
      fillRule="evenodd"
    />
    <path
      d="M18.5 1.25C18.8138 1.25 19.0945 1.4454 19.2034 1.73972L19.4613 2.43675C19.8233 3.4151 19.9388 3.68091 20.1289 3.87106C20.3191 4.06121 20.5849 4.17667 21.5633 4.53869L22.2603 4.79661C22.5546 4.90552 22.75 5.18617 22.75 5.5C22.75 5.81383 22.5546 6.09448 22.2603 6.20339L21.5633 6.46131C20.5849 6.82333 20.3191 6.93879 20.1289 7.12894C19.9388 7.31909 19.8233 7.5849 19.4613 8.56325L19.2034 9.26028C19.0945 9.5546 18.8138 9.75 18.5 9.75C18.1862 9.75 17.9055 9.5546 17.7966 9.26028L17.5387 8.56325C17.1767 7.5849 17.0612 7.31909 16.8711 7.12894C16.6809 6.93879 16.4151 6.82333 15.4367 6.46131L14.7397 6.20339C14.4454 6.09448 14.25 5.81383 14.25 5.5C14.25 5.18617 14.4454 4.90552 14.7397 4.79661L15.4367 4.53869C16.4151 4.17667 16.6809 4.06121 16.8711 3.87106C17.0612 3.68091 17.1767 3.4151 17.5387 2.43675L17.7966 1.73972C17.9055 1.4454 18.1862 1.25 18.5 1.25Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * The installation brand at the top of the sidebar -- image + text label.
 *
 * BOTH VALUES COME FROM THE ONE BRANDING READER mounted at the SPA root (UI-004,
 * `apps/workspace/App.tsx`): `logo_url` is the image source and `product_name` is
 * BOTH the image's accessible name and the text label beside it. This component
 * fetches nothing (SPEC-FRONTEND-RULE-077) and holds no product name of its own
 * (RULE-012); the app suffix under the name stays a Lingui string (D-BR-08), which
 * is why `<Trans>Workspace</Trans>` below is untouched.
 *
 * FAIL-OPEN, AND IT IS THREE POSTURES, NOT ONE (RULE-001, D-BR-15). The provider
 * hands back `DefaultBranding` both while the bounded read is in flight
 * (`loading`) and after it failed (`unavailable`), so those two render the
 * built-in logo and the built-in name silently, with no console line and no
 * rendered error. The THIRD posture is a record that WAS read and carries no logo
 * (`logo_url: null`, CONTRACT-001), which the context cannot cover for us: that is
 * what the `defaults.ts` fallback below is for. A `null` src would render a broken
 * image and a dropped image would leave an empty sidebar header - the RULE-042
 * degraded state a cosmetic failure must never reach.
 *
 * `web/src/branding/defaults.ts` is the ONE file allowed to hold the vendor
 * literal (RULE-012), so the built-in path is IMPORTED here and never retyped.
 *
 * The name, the collapsed / expanded layout, the sizes and objectFit are
 * deliberately unchanged: only the two values moved.
 *
 * @see SPEC-PLATFORM-BRANDING-RULE-001 / RULE-012
 * @see SPEC-PLATFORM-BRANDING-UI-004
 * @see SPEC-PLATFORM-BRANDING-SCENARIO-005
 * @see TASK-PB-BRAND-WORKSPACE-SHELL-CHG-001
 */
const VerisaLogo = ({ collapsed }: { collapsed: boolean }) => {
  const { branding } = useBranding();
  // `Branding.logo_url` is nullable by CONTRACT-001, and so is the TYPE of the
  // built-in field although its value never is; the trailing `?? undefined` is
  // that type's tail rather than a third fallback - `src={null}` is not something
  // an <img> can carry.
  const logoSrc = branding.logo_url ?? DefaultBranding.logo_url ?? undefined;
  const productName = branding.product_name || DefaultBranding.product_name;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: collapsed ? 0 : 2,
        py: 2,
        justifyContent: 'center',
      }}
    >
      {/* The accessible name is the product name in BOTH states (RULE-046): the
          collapsed sidebar drops the text label, so this `alt` is the only name
          the header has there. */}
      <Box
        component="img"
        src={logoSrc}
        alt={productName}
        sx={{
          height: 40,
          width: 40,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />

      {!collapsed && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: colors.text.primary,
              lineHeight: 1.2,
            }}
          >
            {productName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontSize: '11px',
            }}
          >
            <Trans>Workspace</Trans>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Extract agentId from the current URL path if on an agent page.
 * Matches /agents/:agentId and /agents/:agentId/*
 */
const extractAgentId = (pathname: string): string | null => {
  const match = pathname.match(/^\/agents\/([^/]+)/);
  return match ? match[1] : null;
};

/** Global navigation items (always visible) */
const getGlobalNavItems = (label: string): NavItemData[] => [
  {
    text: label,
    icon: <DashboardIcon />,
    path: '/agents',
    matchPaths: ['/agents/'],
  },
];

/** Agent-specific navigation items (only when viewing an agent) */
const getAgentNavItems = (agentId: string, label: string): NavItemData[] => [
  {
    text: label,
    icon: <HistoryIcon />,
    path: `/agents/${agentId}/report-history`,
  },
];

/**
 * Sidebar renders the main navigation drawer.
 * Responsive behavior:
 * - Desktop (>= 960px): Permanent drawer, expanded
 * - Tablet (600-959px): Permanent drawer, collapsed (icons only)
 * - Mobile (< 600px): Temporary drawer, opens via hamburger menu
 */
const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileToggle,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { _: lingui } = useLingui();
  const { user, logout } = useAuth();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const currentAgentId = extractAgentId(location.pathname);

  // Fetch agent name and plugin_id when viewing an agent page
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentPluginId, setAgentPluginId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentAgentId) {
      setAgentName(null);
      setAgentPluginId(null);
      return;
    }
    let cancelled = false;
    getAgent(currentAgentId)
      .then((agent) => {
        if (cancelled) return;
        setAgentName(agent.name || null);
        setAgentPluginId(agent.plugin_id ?? null);
      })
      .catch((err) => {
        console.error("[Sidebar] Failed to load agent metadata:", err);
        if (!cancelled) {
          setAgentName(null);
          setAgentPluginId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentAgentId]);

  // Determine if sidebar should be collapsed (tablet mode)
  const isCollapsed = isTablet;
  const currentWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  /**
   * Renders the drawer content.
   * @param collapsed - Whether to render in collapsed (icon-only) mode
   */
  const renderDrawerContent = (collapsed: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: colors.background.secondary,
      }}
    >
      {/* Logo Section */}
      <VerisaLogo collapsed={collapsed} />

      {/* Navigation Items */}
      <Box
        component="nav"
        sx={{
          flex: 1,
          px: collapsed ? 1 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        aria-label={lingui(msg`Main navigation`)}
      >
        {/* Global nav items */}
        {getGlobalNavItems(lingui(msg`Agents`)).map((item) => (
          <NavItem key={item.path} item={item} collapsed={collapsed} />
        ))}

        {/* Divider always visible below global nav */}
        <Divider sx={{ my: 1, borderColor: colors.border.default }} />

        {/* Agent-specific section (agent name header + nav items) */}
        {currentAgentId && (
          <>
            {/* Agent name label */}
            {agentName && !collapsed && (
              <Box
                sx={{
                  mx: 0,
                  mb: 0.5,
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: colors.text.primary,
                  color: '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {agentName}
                </Typography>
              </Box>
            )}
            {/*
              Report History is a SHIPPED feature (not an in-flight release
              toggle — it had no entry in the legacy visibility map, so it
              resolved visible). Under the fail-closed Feature Gate it carries
              no toggle and renders unconditionally for the doc-processor
              plugin. `agentPluginId === 'doc-processor'` is a plugin-kind
              condition, NOT a feature toggle — it is preserved.
            */}
            {agentPluginId === 'doc-processor' &&
              getAgentNavItems(currentAgentId, lingui(msg`Report History`)).map(
                (item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    collapsed={collapsed}
                  />
                ),
              )}
          </>
        )}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto' }}>
        {/* Locale Picker */}
        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            pb: 1,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <LocalePicker />
        </Box>

        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            pb: 1,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <FeatureGate feature="global.accessibility">
            <AccessibilityDrawer />
          </FeatureGate>
        </Box>

        <Divider sx={{ borderColor: colors.border.default }} />

        {/* User Info (inline) */}
        <Box
          sx={{
            p: collapsed ? 1 : 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: collapsed ? 'center' : 'space-between',
          }}
        >
          {!collapsed && (
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: colors.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {user?.name ?? lingui(msg`User`)}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={logout}
            aria-label={lingui(msg`Log out`)}
            sx={{
              color: colors.text.secondary,
              '&:hover': { color: colors.text.primary },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer - Temporary */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {renderDrawerContent(false)}
      </Drawer>

      {/* Desktop/Tablet Drawer - Permanent.
          IN FLOW, NOT VIEWPORT-FIXED (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29).
          MUI's drawer paper is `position: fixed; top: 0` by default, so this
          navigation ran to the bottom edge of the VIEWPORT and AppLayout had to
          offset its content with a `margin-left` to clear it. Both halves go:
          `position: relative` puts the paper in the flex row, where it occupies
          its own width and takes its height from the row, so the navigation ENDS
          where the row ends - above the shell's full-width bottom bar - and the
          content region simply takes the rest of the row (AppLayout, CHG-004).
          The docked root is the row's own flex item and stretches to the row's
          height, which is what the paper's `height: 100%` resolves against.
          The TEMPORARY drawer above is an overlay and is NOT changed. */}
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', sm: 'block' } }}
        // THE PAPER'S OWN sx, not a `& .MuiDrawer-paper` descendant selector on
        // the root. `position: relative` has to beat MUI's own
        // `position: fixed` on the paper (Drawer.js). A descendant selector on
        // the root wins that on SPECIFICITY, which a real browser applies and
        // jsdom does not: jsdom resolves the cascade in insertion order alone,
        // and the paper's base class is inserted when the paper renders, after
        // the root's - so `fixed` won there and the flow layout was provable
        // only in a browser we do not have. Through the slot the declaration
        // lands on the paper's OWN sx class, which is inserted after that base
        // class, so it wins under both resolution models.
        // `slotProps.paper` is the v6 API; `PaperProps` is deprecated.
        slotProps={{
          paper: {
            sx: {
              boxSizing: 'border-box',
              width: currentWidth,
              position: 'relative',
              height: '100%',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          },
        }}
        open
      >
        {renderDrawerContent(isCollapsed)}
      </Drawer>
    </>
  );
};

export default Sidebar;
